import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';

// assets
import { IconLogout, IconSettings, IconUser } from '@tabler/icons-react';
import { adminApi } from 'services/api';

// ==============================|| PROFILE MENU ||============================== //

export default function ProfileSection() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning,';
    if (hour < 18) return 'Good Afternoon,';
    return 'Good Evening,';
  };

  const [adminName, setAdminName] = useState('Admin');
  const [adminRole, setAdminRole] = useState('Administrator');
  const [avatarUrl, setAvatarUrl] = useState('');

  const loadProfile = async () => {
    try {
      let userInfo = JSON.parse(localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo') || '{}');
      if (userInfo.name || userInfo.account || userInfo.username) {
        setAdminName(userInfo.name || userInfo.account || userInfo.username);
      }
      if (userInfo.avatar) {
        setAvatarUrl(userInfo.avatar);
      }
      const roleStr =
        userInfo.roleName ||
        (userInfo.role === 1 ? 'Super Admin' : userInfo.role === 2 ? 'Admin' : userInfo.role === 3 ? 'Manager' : 'Administrator');
      setAdminRole(roleStr);

      // Fetch live full profile from server (which includes latest avatar, name, and role)
      try {
        const res = await adminApi.getProfile();
        if (res.data && res.data.code === 0 && res.data.value) {
          const serverUser = res.data.value;
          userInfo = { ...userInfo, ...serverUser };
          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          if (serverUser.avatar) {
            setAvatarUrl(serverUser.avatar);
          }
          if (serverUser.name) {
            setAdminName(serverUser.name);
          }
          if (serverUser.roleName || serverUser.role !== undefined) {
            setAdminRole(
              serverUser.roleName ||
                (serverUser.role === 1 ? 'Super Admin' : serverUser.role === 2 ? 'Admin' : serverUser.role === 3 ? 'Manager' : 'Administrator')
            );
          }
        }
      } catch (err) {
        if (userInfo.id && !userInfo.avatar) {
          try {
            const resOne = await adminApi.getOne(userInfo.id);
            const serverUserOne = resOne.data?.value || resOne.data?.data;
            if (serverUserOne) {
              userInfo = { ...userInfo, ...serverUserOne };
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
              if (serverUserOne.avatar) setAvatarUrl(serverUserOne.avatar);
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadProfile();
    window.addEventListener('user-profile-updated', loadProfile);
    window.addEventListener('storage', loadProfile);
    return () => {
      window.removeEventListener('user-profile-updated', loadProfile);
      window.removeEventListener('storage', loadProfile);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    window.location.href = '/login';
  };

  return (
    <>
      <Tooltip title="Account Profile" arrow>
        <ButtonBase
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            borderRadius: '50%',
            p: '2px',
            bgcolor: open ? '#F0FDFA' : 'transparent',
            color: open ? '#0D9488' : '#475569',
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: open ? '2px solid #0D9488' : '2px solid transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.15)' : '#F1F5F9',
              borderColor: '#0D9488'
            }
          }}
        >
          {avatarUrl ? (
            <Avatar
              src={avatarUrl}
              alt={adminName}
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                objectFit: 'cover',
                border: theme.palette.mode === 'dark' ? '1px solid #334155' : '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: '#0D9488',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                border: '1px solid #0F766E'
              }}
            >
              {(adminName || 'A').charAt(0).toUpperCase()}
            </Avatar>
          )}

          {/* Semantic Green Online Indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 1,
              right: 1,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#10B981',
              border: `2px solid ${theme.palette.mode === 'dark' ? (theme.palette.background?.header || '#15171C') : '#FFFFFF'}`,
              boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.3)'
            }}
          />
        </ButtonBase>
      </Tooltip>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  borderRadius: '12px',
                  border: theme.palette.mode === 'dark' ? (theme.palette.border?.default ? `1px solid ${theme.palette.border.default}` : '1px solid rgba(255, 255, 255, 0.08)') : '1px solid #E2E8F0',
                  boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0, 0, 0, 0.45)' : '0 4px 20px rgba(0,0,0,0.08)',
                  bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.raised || '#272B33') : '#FFFFFF'
                }}
              >
                {open && (
                  <MainCard
                    border={false}
                    elevation={0}
                    content={false}
                    sx={{
                      minWidth: 260,
                      bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.raised || '#272B33') : '#FFFFFF'
                    }}
                  >
                    <Box sx={{ p: 2, pb: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={avatarUrl}
                          alt={adminName}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: theme.palette.mode === 'dark' ? '2px solid #0D9488' : '2px solid #CCFBF1',
                            bgcolor: '#0D9488',
                            color: '#FFFFFF',
                            fontWeight: 500,
                            fontSize: '16px'
                          }}
                        >
                          {!avatarUrl && (adminName || 'A').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              color: theme.palette.mode === 'dark' ? (theme.palette.text?.secondary || '#A4ABB8') : '#64748B',
                              fontSize: '12px',
                              lineHeight: 1.2
                            }}
                          >
                            {getGreeting()}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontWeight: 500,
                              color: theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#1E293B',
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {adminName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              color: theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488',
                              fontWeight: 500,
                              fontSize: '11px',
                              display: 'block',
                              mt: 0.2
                            }}
                          >
                            {adminRole}
                          </Typography>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 1.5, borderColor: theme.palette.mode === 'dark' ? (theme.palette.border?.subtle || 'rgba(255, 255, 255, 0.05)') : '#F1F5F9' }} />
                    </Box>

                    <Box sx={{ px: 1, pb: 1 }}>
                      <List component="nav" sx={{ p: 0 }}>
                        <ListItemButton
                          sx={{
                            borderRadius: '6px',
                            py: 1,
                            px: 1.5,
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.hover || '#2A2E36') : '#F8FAFC',
                              '& .MuiListItemIcon-root': { color: '#14B8A6' },
                              '& .MuiTypography-root': { color: '#14B8A6' }
                            }
                          }}
                          onClick={() => {
                            setOpen(false);
                            navigate('/account-settings');
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: theme.palette.mode === 'dark' ? (theme.palette.text?.secondary || '#A4ABB8') : '#64748B' }}>
                            <IconSettings stroke={1.75} size={18} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#1E293B' }}>
                                Account Settings
                              </Typography>
                            }
                          />
                        </ListItemButton>

                        <ListItemButton
                          sx={{
                            borderRadius: '6px',
                            py: 1,
                            px: 1.5,
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#FEF2F2',
                              '& .MuiListItemIcon-root': { color: '#EF4444' },
                              '& .MuiTypography-root': { color: '#EF4444' }
                            }
                          }}
                          onClick={handleLogout}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: theme.palette.mode === 'dark' ? (theme.palette.text?.secondary || '#A4ABB8') : '#64748B' }}>
                            <IconLogout stroke={1.75} size={18} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#1E293B' }}>
                                Logout
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
